import { H2, P, Ul, Code, DocTable, RiskBadge } from '../../components/docs/primitives';
import { INDEX_REPO, PUBLISHING_URL, SPEC } from './links';

export default function Overview() {
  return (
    <>
      <H2 id="how-it-works">How it works</H2>
      <P>
        A plugin is a separate program, not code loaded into the app. StreamNook spawns it as its
        own process and talks to it over JSON-RPC 2.0 on stdin and stdout. The app ships with zero
        plugins built in; everything is opt-in from the marketplace under Settings and Plugins.
      </P>
      <Ul>
        <li>
          The process boundary is the isolation. A crashing or misbehaving plugin cannot corrupt
          the app, and disabling one shuts its process down.
        </li>
        <li>Plugins can be written in any language that can read stdin and write stdout.</li>
        <li>
          A plugin's network traffic originates from its own process and must be declared in its
          manifest. The host provides no HTTP access at all.
        </li>
        <li>No plugin code runs before its signatures verify and the user consents.</li>
      </Ul>

      <H2 id="surface">What a plugin gets</H2>
      <P>
        The host exposes a deliberately small surface, and nothing is granted by default. A plugin
        receives exactly what its manifest requests and the user approves; everything else is
        denied.
      </P>
      <DocTable
        head={['Surface', 'What it covers']}
        rows={[
          [
            'Events',
            'Playback start/stop, channel changes, a periodic watch tick, live followed channels, ad detection state, settings panel changes.',
          ],
          [
            'Host methods',
            <>
              <Code>get_followed_live</Code>, <Code>set_upstream</Code>, <Code>notify</Code>,{' '}
              <Code>log</Code>, and settings panel registration.
            </>,
          ],
          [
            'Credentials',
            'A Twitch token, only behind a separate consent prompt on first use, with every handover written to an audit log.',
          ],
          [
            'UI',
            'A settings panel rendered by the host from a schema. Plugins never get arbitrary UI access.',
          ],
        ]}
      />

      <H2 id="risk-labels">Capability labels</H2>
      <P>Every listing carries one of three labels, shown before you install:</P>
      <DocTable
        head={['Label', 'Meaning']}
        rows={[
          [
            <RiskBadge tier="A" />,
            'Official interfaces, local features, and ordinary viewer actions. Theme packs, layout tools, local stats.',
          ],
          [
            <RiskBadge tier="B" />,
            'Talks to Twitch or other services over interfaces they do not officially document, the way a normal viewer would. Alternate emote providers, community badge sources.',
          ],
          [
            <RiskBadge tier="C" />,
            'Runs background automation or changes how playback is resolved. Community-distributed, and kept out of the official index.',
          ],
        ]}
      />

      <H2 id="sources">Where plugins come from</H2>
      <P>
        The official index is the source StreamNook trusts out of the box. It is curated, lists
        only the first two labels, and is where official plugins (authored by the StreamNook
        project, signed with its key, shown verified) live.
      </P>
      <P>
        Community sources are indexes run by anyone, added explicitly by you. They use the same
        signed format and verification, but StreamNook does not review, host, or endorse what they
        list, so only add sources you trust. Adding one pins the operator's key, so a
        hijacked source fails loudly instead of updating quietly.
      </P>

      <H2 id="spec">The specification</H2>
      <P>
        Protocol version 1 is frozen: breaking changes require a version bump, additive changes do
        not, and both sides ignore unknown fields. The full spec ships with the StreamNook source
        under <Code>docs/plugins</Code>:
      </P>
      <Ul>
        <li>
          <a href={SPEC.protocol} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            PROTOCOL.md
          </a>{' '}
          wire format, lifecycle, events, host methods, errors
        </li>
        <li>
          <a href={SPEC.manifest} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            MANIFEST.md
          </a>{' '}
          the plugin.toml schema and capability tiers
        </li>
        <li>
          <a href={SPEC.capabilities} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            CAPABILITIES.md
          </a>{' '}
          the capability vocabulary and exact consent language
        </li>
        <li>
          <a href={SPEC.signing} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            SIGNING.md
          </a>{' '}
          artifact signing, the index format, key pinning
        </li>
        <li>
          <a href={SPEC.official} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            OFFICIAL.md
          </a>{' '}
          what an official plugin is and can never be
        </li>
        <li>
          <a href={PUBLISHING_URL} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            PUBLISHING.md
          </a>{' '}
          the author walkthrough in the{' '}
          <a href={INDEX_REPO} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            index repository
          </a>
        </li>
      </Ul>
    </>
  );
}
